import {
  Autocomplete,
  type Item,
  SelectInput,
  SelectInputItem,
  Switch,
  TextField,
} from '@ads/components-react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useGetApiV1LocationCitiesDropdown,
  useGetApiV1LocationCountriesDropdown,
  useGetApiV1LocationStatesDropdown,
  useGetApiV1PersonSharedGenderDropdown,
  useGetApiV1PersonSharedMaritalStatusDropdown,
} from '@/shared/api/generated';
import { DatePicker, Textarea } from '@/shared/ui/atoms';
import { FormField } from '@/shared/ui/molecules';
import { normalizeRG } from '@/shared/utils/rg';
import { applyProgressiveCpfMask } from '@/shared/utils/string';

import { numToStr, strToNum, type TabProps } from '../personFormSchema';

const BRAZIL_ID = 1;

export function PersonalTab({ control, register, errors, setValue }: TabProps) {
  const { t } = useTranslation('personManagement');

  const isPCD = useWatch({ control, name: 'isPCD' });
  const useAccessibilityTool = useWatch({
    control,
    name: 'useAccessibilityTool',
  });
  const birthCountryId = useWatch({ control, name: 'birthCountryId' });
  const birthStateId = useWatch({ control, name: 'birthStateId' });
  const isBrazil = birthCountryId === BRAZIL_ID;

  const { data: genderOptions = [] } = useGetApiV1PersonSharedGenderDropdown();
  const { data: maritalStatusOptions = [] } = useGetApiV1PersonSharedMaritalStatusDropdown();
  const { data: countryOptions = [] } = useGetApiV1LocationCountriesDropdown();
  const { data: stateOptions = [] } = useGetApiV1LocationStatesDropdown(
    birthCountryId !== undefined ? { countryId: birthCountryId } : undefined,
    { query: { enabled: !!birthCountryId && isBrazil } },
  );
  const { data: cityOptions = [] } = useGetApiV1LocationCitiesDropdown(
    birthStateId !== undefined ? { stateId: birthStateId } : undefined,
    { query: { enabled: !!birthStateId && isBrazil } },
  );

  const countryItems: Item<number>[] = countryOptions.map(({ key, value }) => ({
    label: value ?? '',
    value: key ?? 0,
  }));
  const stateItems: Item<number>[] = stateOptions.map(({ key, value }) => ({
    label: value ?? '',
    value: key ?? 0,
  }));
  const cityItems: Item<number>[] = cityOptions.map(({ key, value }) => ({
    label: value ?? '',
    value: key ?? 0,
  }));

  const selectedCountry = countryItems.find(i => i.value === birthCountryId) ?? null;
  const selectedState = stateItems.find(i => i.value === birthStateId) ?? null;
  const birthCityId = useWatch({ control, name: 'birthCityId' });
  const selectedCity = cityItems.find(i => i.value === birthCityId) ?? null;

  return (
    <div className="flex flex-col gap-xxs">
      {/* Nome */}
      <div className="grid grid-cols-1 gap-mini @2xl:grid-cols-3">
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.firstName')}
          placeholder={t('personForm.fields.firstName')}
          hasError={!!errors.firstName}
          {...(errors.firstName?.message ? { helper: errors.firstName.message } : {})}
          {...register('firstName')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.lastName')}
          placeholder={t('personForm.fields.lastName')}
          hasError={!!errors.lastName}
          {...(errors.lastName?.message ? { helper: errors.lastName.message } : {})}
          {...register('lastName')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.alias')}
          placeholder={t('personForm.fields.alias')}
          {...register('alias')}
        />
      </div>

      {/* E-mail e CPF */}
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <TextField
          fullWidth
          size="md"
          type="email"
          label={t('personForm.fields.email')}
          placeholder={t('personForm.fields.email')}
          hasError={!!errors.email}
          {...(errors.email?.message ? { helper: errors.email.message } : {})}
          {...register('email')}
        />
        <Controller
          name="cpf"
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              size="md"
              label={t('personForm.fields.cpf')}
              placeholder="000.000.000-00"
              hasError={!!errors.cpf}
              {...(errors.cpf?.message ? { helper: errors.cpf.message } : {})}
              {...field}
              value={field.value ?? ''}
              onChange={e => field.onChange(applyProgressiveCpfMask(e.target.value))}
            />
          )}
        />
      </div>

      {/* Nascimento, gênero, estado civil */}
      <div className="grid grid-cols-1 gap-mini @2xl:grid-cols-3">
        <Controller
          name="birthDate"
          control={control}
          render={({ field }) => (
            <DatePicker
              size="md"
              label={t('personForm.fields.birthDate')}
              value={field.value ?? ''}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <SelectInput
              size="md"
              label={t('personForm.fields.gender')}
              placeholder={t('personForm.fields.gender')}
              value={numToStr(field.value)}
              onValueChange={v => field.onChange(strToNum(v))}
            >
              {genderOptions.map(({ key, value }) => (
                <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                  {value ?? ''}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
        <Controller
          name="martialStatus"
          control={control}
          render={({ field }) => (
            <SelectInput
              size="md"
              label={t('personForm.fields.martialStatus')}
              placeholder={t('personForm.fields.martialStatus')}
              value={numToStr(field.value)}
              onValueChange={v => field.onChange(strToNum(v))}
            >
              {maritalStatusOptions.map(({ key, value }) => (
                <SelectInputItem key={key} value={String(key)} custom={{ root: 'text-nowrap' }}>
                  {value ?? ''}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
      </div>

      {/* Local de nascimento */}
      <div className="grid grid-cols-1 gap-mini @2xl:grid-cols-3">
        <Autocomplete<number>
          size="md"
          label={t('personForm.fields.birthCountry')}
          placeholder={t('personForm.fields.birthCountry')}
          list={countryItems}
          {...(selectedCountry !== null ? { selectedItem: selectedCountry } : {})}
          handleChange={item => {
            setValue?.('birthCountryId', item?.value ?? undefined);
            setValue?.('birthStateId', undefined);
            setValue?.('birthCityId', undefined);
            setValue?.('birthStateName', null);
            setValue?.('birthCityName', null);
          }}
        />
        {isBrazil ? (
          <Autocomplete<number>
            size="md"
            label={t('personForm.fields.birthState')}
            placeholder={t('personForm.fields.birthState')}
            list={stateItems}
            {...(selectedState !== null ? { selectedItem: selectedState } : {})}
            handleChange={item => {
              setValue?.('birthStateId', item?.value ?? undefined);
              setValue?.('birthCityId', undefined);
              setValue?.('birthCityName', null);
            }}
          />
        ) : (
          <TextField
            fullWidth
            size="md"
            label={t('personForm.fields.birthState')}
            placeholder={t('personForm.fields.birthState')}
            {...register('birthStateName')}
          />
        )}
        {isBrazil ? (
          <Autocomplete<number>
            size="md"
            label={t('personForm.fields.birthCity')}
            placeholder={t('personForm.fields.birthCity')}
            list={cityItems}
            {...(selectedCity !== null ? { selectedItem: selectedCity } : {})}
            handleChange={item => {
              setValue?.('birthCityId', item?.value ?? undefined);
            }}
          />
        ) : (
          <TextField
            fullWidth
            size="md"
            label={t('personForm.fields.birthCity')}
            placeholder={t('personForm.fields.birthCity')}
            {...register('birthCityName')}
          />
        )}
      </div>

      {/* Filiação */}
      <div className="grid grid-cols-1 gap-mini @2xl:grid-cols-3">
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.mothersName')}
          placeholder={t('personForm.fields.mothersName')}
          {...register('mothersName')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.fathersName')}
          placeholder={t('personForm.fields.fathersName')}
          {...register('fathersName')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.spouseName')}
          placeholder={t('personForm.fields.spouseName')}
          {...register('spouseName')}
        />
      </div>

      {/* Documentos */}
      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2 @3xl:grid-cols-4">
        <Controller
          name="rg"
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              size="md"
              label={t('personForm.document.rgSection')}
              placeholder={t('personForm.document.rgSection')}
              hasError={!!errors.rg}
              {...(errors.rg?.message ? { helper: errors.rg.message } : {})}
              {...field}
              value={field.value ?? ''}
              onChange={e => field.onChange(normalizeRG(e.target.value))}
            />
          )}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.document.dispatcherOrgan')}
          placeholder={t('personForm.document.dispatcherOrgan')}
          {...register('dispatcherOrgan')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.document.dispatcherOrganState')}
          placeholder="SP"
          maxLength={2}
          {...register('dispatcherOrganState')}
        />
        <Controller
          name="dateDispatch"
          control={control}
          render={({ field }) => (
            <DatePicker
              size="md"
              label={t('personForm.document.dispatchDate')}
              value={field.value ?? ''}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-mini @md:grid-cols-2">
        <TextField
          fullWidth
          size="md"
          label={t('personForm.document.passportSection')}
          placeholder={t('personForm.document.passportSection')}
          {...register('passportNumber')}
        />
        <TextField
          fullWidth
          size="md"
          label={t('personForm.fields.idCountryNumber')}
          placeholder={t('personForm.fields.idCountryNumber')}
          {...register('idCountryNumber')}
        />
      </div>

      {/* Acessibilidade */}
      <div className="flex flex-col gap-nano">
        <h3 className="ts-heading-xs text-ctx-content-title">
          {t('personForm.fields.accessibilitySection')}
        </h3>
        <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border p-mini">
          <Controller
            name="isPCD"
            control={control}
            render={({ field }) => (
              <Switch
                label={t('personForm.fields.isPCD')}
                checked={field.value === true}
                onCheckedChange={field.onChange}
              />
            )}
          />
          {isPCD && (
            <Controller
              name="pcdDescription"
              control={control}
              render={({ field }) => (
                <FormField label={t('personForm.fields.pcdDescription')}>
                  <Textarea
                    placeholder={t('personForm.fields.pcdDescription')}
                    value={(field.value as string) ?? ''}
                    onChange={e => field.onChange(e.target.value)}
                    rows={2}
                  />
                </FormField>
              )}
            />
          )}
          <Controller
            name="useAccessibilityTool"
            control={control}
            render={({ field }) => (
              <Switch
                label={t('personForm.fields.useAccessibilityTool')}
                checked={field.value === true}
                onCheckedChange={field.onChange}
              />
            )}
          />
          {useAccessibilityTool && (
            <Controller
              name="accessibilityToolDescription"
              control={control}
              render={({ field }) => (
                <FormField label={t('personForm.fields.accessibilityToolDescription')}>
                  <Textarea
                    placeholder={t('personForm.fields.accessibilityToolDescription')}
                    value={(field.value as string) ?? ''}
                    onChange={e => field.onChange(e.target.value)}
                    rows={2}
                  />
                </FormField>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}
